import { api } from "./api.js";
import type { ComposerSender, MailCardPreview } from "./mail.js";

/**
 * The operator's signature for automatic mail (`crm-email/system-signature.routes.ts`).
 *
 * Its own file rather than a few more lines in `mail.ts`: that one is the
 * mailbox's client (threads, drafts, folders), and this is the signatures:
 * the company card above, and each person's own below.
 */
export interface SignatureDraft {
  closing: string;
  name: string;
  details: string;
  /** The bar beside the name, `#rrggbb`; "" for a neutral grey. */
  accent: string;
  /** A media-library address; "" for no icon. */
  logoSource: string;
}

export interface SystemSignatureView {
  signature: SignatureDraft & { logoUrl: string; logoWidth: number; logoHeight: number };
  /** Built from the company record and the CRM's own branding; any part may be empty. */
  offer: Pick<SignatureDraft, "name" | "details" | "accent" | "logoSource">;
  limits: { closing: number; name: number; details: number; detailLines: number };
  /** The host site, whose media library the icon is picked from. */
  mediaSiteId: string | null;
}

export const getSystemSignature = () => api.get<SystemSignatureView>("/admin/crm/mail/system-signature");

export const saveSystemSignature = (draft: SignatureDraft) =>
  api.put<SystemSignatureView>("/admin/crm/mail/system-signature", draft);

// ── A person's own signatures and sender (`crm-email/user-mail-prefs.routes.ts`) ──

/** One of the writer's own signatures. Fields rather than free text, so the
 *  company card can carry the name and lines instead of repeating them. */
export interface OwnSignature {
  id: string;
  /** What the picker calls it. */
  label: string;
  closing: string;
  name: string;
  details: string;
  /** End with the company card: its mark, colour bar, name and contacts. */
  card: boolean;
}

export interface MyMailSettings {
  signatures: OwnSignature[];
  defaultId: string;
  /** The writer's own default sender, or "" for the mailbox's default. */
  fromAddress: string;
  /** The addresses the mailbox lets a letter leave from. */
  senders: ComposerSender[];
  mailboxDefault: string;
  /** The company card as it is; null when nobody has filled it in. */
  company: MailCardPreview | null;
  limits: { list: number; label: number; closing: number; name: number; details: number; detailLines: number };
}

export const getMyMailSettings = () => api.get<MyMailSettings>("/admin/crm/mail/my-settings");

export const saveMyMailSettings = (body: { signatures: OwnSignature[]; defaultId: string; fromAddress: string }) =>
  api.put<MyMailSettings>("/admin/crm/mail/my-settings", body);

/** One signature as the composer draws it: what the send will append, already joined. */
export interface ComposerSignature {
  id: string;
  /** Empty on the legacy single shared one; the interface labels that itself. */
  name: string;
  /** The grey text, or empty. */
  text: string;
  /** The card under it, or null. */
  card: MailCardPreview | null;
  /** The card is switched on but the company card is empty, so none is added. */
  cardMissing: boolean;
  /** The end of the letter's HTML part, exactly as the send appends it. */
  html: string;
}

/**
 * What the composer picks from: the writer's own signatures, or the shared ones
 * while they have none (`own` says which).
 */
export const getMailSignatures = () =>
  api.get<{ signatures: ComposerSignature[]; defaultSignatureId: string; own: boolean }>("/admin/crm/mail/signatures");
